import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { apprenantService } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { UserPlus, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Badge } from './ui/badge';

const FREINS_OPTIONS = ['Mobilité', 'Logement', 'Financier', 'Santé', 'Juridique', 'Addiction', 'Administratif', 'Autres'];
const SECTEURS_OPTIONS = ['Hôtellerie', 'BTP', 'Agriculture', 'Commerce', 'Industrie', 'Services', 'Santé', 'Transport', 'Autre'];

const NIVEAUX_ETUDES = [
  { value: 'sans_diplome', label: 'Sans diplôme' },
  { value: 'brevet', label: 'Brevet des collèges' },
  { value: 'cap_bep', label: 'CAP / BEP' },
  { value: 'bac', label: 'Baccalauréat' },
  { value: 'bac_plus_2', label: 'Bac +2 (BTS, DUT)' },
  { value: 'bac_plus_3', label: 'Bac +3 (Licence)' },
  { value: 'bac_plus_5', label: 'Bac +5 (Master)' },
  { value: 'doctorat', label: 'Doctorat' }
];

const SITUATIONS_ACTUELLES = [
  { value: 'demandeur_emploi', label: 'Demandeur d\'emploi' },
  { value: 'rsa', label: 'Bénéficiaire RSA' },
  { value: 'are', label: 'ARE (Allocation Retour Emploi)' },
  { value: 'ass', label: 'ASS (Allocation Solidarité Spécifique)' },
  { value: 'aah', label: 'AAH (Allocation Adulte Handicapé)' },
  { value: 'salarie', label: 'Salarié en reconversion' },
  { value: 'etudiant', label: 'Étudiant' },
  { value: 'autre', label: 'Autre' }
];

const SITUATIONS_FAMILIALES = [
  { value: 'celibataire', label: 'Célibataire' },
  { value: 'marie', label: 'Marié(e)' },
  { value: 'pacse', label: 'Pacsé(e)' },
  { value: 'divorce', label: 'Divorcé(e)' },
  { value: 'veuf', label: 'Veuf(ve)' },
  { value: 'concubinage', label: 'Concubinage' }
];

const MOBILITES = [
  { value: 'locale', label: 'Locale (commune)' },
  { value: 'departementale', label: 'Départementale' },
  { value: 'regionale', label: 'Régionale' },
  { value: 'nationale', label: 'Nationale' },
  { value: 'internationale', label: 'Internationale' }
];

const AddApprenantDialog = ({ onSuccess, editMode = false, bénéficiaireToEdit = null, triggerButton = null }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(editMode); // En mode édition, on affiche tout
  const [referentsList, setReferentsList] = useState([]);
  const [animateursList, setAnimateursList] = useState([]);
  
  // Charger les listes des référents et animateurs
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const [referentsRes, animateursRes] = await Promise.all([
          api.get('/referents'),
          api.get('/animateurs')
        ]);
        setReferentsList(referentsRes.data || []);
        setAnimateursList(animateursRes.data || []);
      } catch (error) {
        console.error('Erreur chargement utilisateurs:', error);
      }
    };
    if (open) {
      loadUsers();
    }
  }, [open]);
  
  const [formData, setFormData] = useState({
    // Informations de base
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    date_naissance: '',
    genre: undefined,
    adresse: '',
    code_postal: '',
    ville: '',
    zone_geographique: 'Saint-Pierre',
    
    // Identifiants administratifs
    numero_securite_sociale: '',
    numero_identifiant_france_travail: '',
    
    // Situation personnelle
    situation_familiale: undefined,
    nombre_enfants: 0,
    nationalite: 'Française',
    
    // Contact d'urgence
    contact_urgence_nom: '',
    contact_urgence_telephone: '',
    contact_urgence_lien: undefined,
    
    // Situation professionnelle
    situation_actuelle: undefined,
    niveau_etudes: undefined,
    diplome_obtenu: '',
    experience_professionnelle: '',
    objectif_professionnel: '',
    
    // Mobilité
    permis_conduire: false,
    vehicule: false,
    mobilite_geographique: '',
    contraintes_horaires: '',
    
    // Aides sociales
    rsa: false,
    are: false,
    ass: false,
    aah: false,
    autres_aides: '',
    
    // Parcours - Affectations
    referent_id: user?.role === 'referent' ? user?.id : '',  // Auto-assign si référent
    animateurs_ids: [],
    secteurs_vises: [],
    secteur_autre_precision: '',
    freins: {},
    commentaires: ''
  });

  // Charger les données en mode édition
  React.useEffect(() => {
    if (editMode && bénéficiaireToEdit && open) {
      setFormData({
        nom: bénéficiaireToEdit.nom || '',
        prenom: bénéficiaireToEdit.prenom || '',
        email: bénéficiaireToEdit.email || '',
        telephone: bénéficiaireToEdit.telephone || '',
        date_naissance: bénéficiaireToEdit.date_naissance || '',
        genre: bénéficiaireToEdit.genre || '',
        adresse: bénéficiaireToEdit.adresse || '',
        code_postal: bénéficiaireToEdit.code_postal || '',
        ville: bénéficiaireToEdit.ville || '',
        zone_geographique: bénéficiaireToEdit.zone_geographique || 'Saint-Pierre',
        numero_securite_sociale: bénéficiaireToEdit.numero_securite_sociale || '',
        numero_identifiant_france_travail: bénéficiaireToEdit.numero_identifiant_france_travail || '',
        situation_familiale: bénéficiaireToEdit.situation_familiale || '',
        nombre_enfants: bénéficiaireToEdit.nombre_enfants || 0,
        nationalite: bénéficiaireToEdit.nationalite || 'Française',
        contact_urgence_nom: bénéficiaireToEdit.contact_urgence_nom || '',
        contact_urgence_telephone: bénéficiaireToEdit.contact_urgence_telephone || '',
        contact_urgence_lien: bénéficiaireToEdit.contact_urgence_lien || '',
        situation_actuelle: bénéficiaireToEdit.situation_actuelle || '',
        niveau_etudes: bénéficiaireToEdit.niveau_etudes || '',
        diplome_obtenu: bénéficiaireToEdit.diplome_obtenu || '',
        experience_professionnelle: bénéficiaireToEdit.experience_professionnelle || '',
        objectif_professionnel: bénéficiaireToEdit.objectif_professionnel || '',
        permis_conduire: bénéficiaireToEdit.permis_conduire || false,
        vehicule: bénéficiaireToEdit.vehicule || false,
        mobilite_geographique: bénéficiaireToEdit.mobilite_geographique || '',
        contraintes_horaires: bénéficiaireToEdit.contraintes_horaires || '',
        rsa: bénéficiaireToEdit.rsa || false,
        are: bénéficiaireToEdit.are || false,
        ass: bénéficiaireToEdit.ass || false,
        aah: bénéficiaireToEdit.aah || false,
        autres_aides: bénéficiaireToEdit.autres_aides || '',
        referent_id: bénéficiaireToEdit.referent_id || user?.id || '',
        animateurs_ids: bénéficiaireToEdit.animateurs_ids || [],
        secteurs_vises: bénéficiaireToEdit.secteurs_vises || bénéficiaireToEdit.secteurs_activite || [],
        secteur_autre_precision: bénéficiaireToEdit.secteur_autre_precision || '',
        freins: bénéficiaireToEdit.freins || {},
        commentaires: bénéficiaireToEdit.commentaires || ''
      });
    }
  }, [editMode, bénéficiaireToEdit, open, user]);

  const toggleFrein = (frein) => {
    setFormData((prev) => {
      const newFreins = { ...prev.freins };
      if (newFreins[frein] !== undefined) {
        delete newFreins[frein];
      } else {
        newFreins[frein] = '';
      }
      return { ...prev, freins: newFreins };
    });
  };

  const updateFreinDetail = (frein, detail) => {
    setFormData((prev) => ({
      ...prev,
      freins: { ...prev.freins, [frein]: detail }
    }));
  };

  const toggleSecteur = (secteur) => {
    setFormData((prev) => {
      const secteurs = prev.secteurs_vises || [];
      if (secteurs.includes(secteur)) {
        // On décoche : si on enlève "Autre", on efface aussi sa précision
        const next = { ...prev, secteurs_vises: secteurs.filter((s) => s !== secteur) };
        if (secteur === 'Autre') {
          next.secteur_autre_precision = '';
        }
        return next;
      } else if (secteurs.length < 3) {
        return { ...prev, secteurs_vises: [...secteurs, secteur] };
      }
      return prev;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation référent obligatoire
      if (!formData.referent_id && user?.role !== 'referent') {
        toast.error('Veuillez sélectionner un référent');
        setLoading(false);
        return;
      }
      
      // Préparer les données avec le mapping correct
      const dataToSend = {
        ...formData,
        referent_id: formData.referent_id || user.id,
        animateurs_ids: formData.animateurs_ids || [],
        secteurs_activite: formData.secteurs_vises // Mapping pour compatibilité backend
      };
      
      if (editMode && bénéficiaireToEdit) {
        await apprenantService.update(bénéficiaireToEdit.id, dataToSend);
        toast.success('Bénéficiaire mis à jour avec succès');
      } else {
        await apprenantService.create(dataToSend);
        toast.success('Bénéficiaire créé avec succès');
      }
      setOpen(false);
      if (!editMode) resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      date_naissance: '',
      genre: undefined,
      adresse: '',
      code_postal: '',
      ville: '',
      zone_geographique: 'Saint-Pierre',
      numero_securite_sociale: '',
      numero_identifiant_france_travail: '',
      situation_familiale: undefined,
      nombre_enfants: 0,
      nationalite: 'Française',
      contact_urgence_nom: '',
      contact_urgence_telephone: '',
      contact_urgence_lien: undefined,
      situation_actuelle: undefined,
      niveau_etudes: undefined,
      diplome_obtenu: '',
      experience_professionnelle: '',
      objectif_professionnel: '',
      permis_conduire: false,
      vehicule: false,
      mobilite_geographique: '',
      contraintes_horaires: '',
      rsa: false,
      are: false,
      ass: false,
      aah: false,
      autres_aides: '',
      referent_id: user?.role === 'referent' ? user?.id : undefined,
      animateurs_ids: [],
      secteurs_vises: [],
      secteur_autre_precision: '',
      freins: {},
      commentaires: ''
    });
    setShowMoreFields(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button data-testid={editMode ? "edit-bénéficiaire-button" : "add-bénéficiaire-button"} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
            <UserPlus className="w-4 h-4 mr-2" />
            {editMode ? 'Modifier' : 'Ajouter'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[700px] rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>
            {editMode ? `Modifier ${bénéficiaireToEdit?.prenom} ${bénéficiaireToEdit?.nom}` : 'Nouveau Bénéficiaire'}
          </DialogTitle>
          <DialogDescription>
            {editMode ? 'Modifiez les informations du bénéficiaire' : 'Remplissez les informations du bénéficiaire (les champs marqués * sont obligatoires)'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Section: Identité */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Identité</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  data-testid="bénéficiaire-nom-input"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prenom">Prénom *</Label>
                <Input
                  id="prenom"
                  data-testid="bénéficiaire-prenom-input"
                  value={formData.prenom}
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-2">
                <Label htmlFor="date_naissance">Date de naissance *</Label>
                <Input
                  id="date_naissance"
                  data-testid="bénéficiaire-date-naissance-input"
                  type="date"
                  value={formData.date_naissance}
                  onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="genre">Genre</Label>
                <Select value={formData.genre} onValueChange={(val) => setFormData({ ...formData, genre: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="homme">Homme</SelectItem>
                    <SelectItem value="femme">Femme</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-3">
              <div className="space-y-2">
                <Label htmlFor="situation_familiale">Situation familiale</Label>
                <Select value={formData.situation_familiale} onValueChange={(val) => setFormData({ ...formData, situation_familiale: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {SITUATIONS_FAMILIALES.map(sf => (
                      <SelectItem key={sf.value} value={sf.value}>{sf.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nombre_enfants">Nombre d'enfants</Label>
                <Input
                  id="nombre_enfants"
                  type="number"
                  min="0"
                  value={formData.nombre_enfants}
                  onChange={(e) => setFormData({ ...formData, nombre_enfants: parseInt(e.target.value) || 0 })}
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Section: Coordonnées */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Coordonnées</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  data-testid="bénéficiaire-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-xl"
                  placeholder="email@exemple.fr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telephone">Téléphone *</Label>
                <Input
                  id="telephone"
                  data-testid="bénéficiaire-telephone-input"
                  type="tel"
                  value={formData.telephone}
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                  required
                  className="rounded-xl"
                  placeholder="06 12 34 56 78"
                />
              </div>
            </div>

            <div className="space-y-2 mt-3">
              <Label htmlFor="adresse">Adresse *</Label>
              <Input
                id="adresse"
                data-testid="bénéficiaire-adresse-input"
                value={formData.adresse}
                onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                required
                className="rounded-xl"
                placeholder="123 rue Exemple"
              />
            </div>

            <div className="grid grid-cols-3 gap-4 mt-3">
              <div className="space-y-2">
                <Label htmlFor="code_postal">Code postal</Label>
                <Input
                  id="code_postal"
                  value={formData.code_postal}
                  onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })}
                  className="rounded-xl"
                  placeholder="97250"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ville">Ville</Label>
                <Input
                  id="ville"
                  value={formData.ville}
                  onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zone">Zone géographique *</Label>
                <Select value={formData.zone_geographique} onValueChange={(val) => setFormData({ ...formData, zone_geographique: val })}>
                  <SelectTrigger data-testid="bénéficiaire-zone-select" className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Saint-Pierre">Saint-Pierre (Nord Caraïbe)</SelectItem>
                    <SelectItem value="Sainte-Marie">Sainte-Marie (Nord Atlantique)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section: Identifiants administratifs */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Identifiants administratifs</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="secu">N° Sécurité Sociale *</Label>
                <Input
                  id="secu"
                  data-testid="bénéficiaire-secu-input"
                  value={formData.numero_securite_sociale}
                  onChange={(e) => setFormData({ ...formData, numero_securite_sociale: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="france-travail">N° Identifiant France Travail *</Label>
                <Input
                  id="france-travail"
                  data-testid="bénéficiaire-france-travail-input"
                  value={formData.numero_identifiant_france_travail}
                  onChange={(e) => setFormData({ ...formData, numero_identifiant_france_travail: e.target.value })}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>
          </div>

          {/* Section: Affectation Référent & Intervenants */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h3 className="text-sm font-bold text-blue-700 mb-3 uppercase tracking-wider">Affectation</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="referent">Référent attitré *</Label>
                <Select 
                  value={formData.referent_id} 
                  onValueChange={(val) => setFormData({ ...formData, referent_id: val })}
                  disabled={user?.role === 'referent'}
                >
                  <SelectTrigger data-testid="bénéficiaire-referent-select" className="rounded-xl">
                    <SelectValue placeholder="Sélectionner un référent" />
                  </SelectTrigger>
                  <SelectContent>
                    {referentsList.length === 0 ? (
                      <div className="p-2 text-sm text-slate-500 text-center">
                        Aucun référent disponible.<br/>
                        <span className="text-xs">Créez d'abord un utilisateur avec le rôle "Référent".</span>
                      </div>
                    ) : (
                      referentsList.map((ref) => (
                        <SelectItem key={ref.id} value={ref.id}>
                          {ref.prenom} {ref.nom}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {user?.role === 'referent' && (
                  <p className="text-xs text-blue-600">Vous êtes automatiquement assigné comme référent</p>
                )}
                {referentsList.length === 0 && user?.role !== 'referent' && (
                  <p className="text-xs text-amber-600">⚠️ Aucun référent n'existe. Créez un compte référent d'abord.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Intervenants assignés</Label>
                <Select 
                  onValueChange={(val) => {
                    if (!formData.animateurs_ids.includes(val)) {
                      setFormData({ ...formData, animateurs_ids: [...formData.animateurs_ids, val] });
                    }
                  }}
                >
                  <SelectTrigger data-testid="bénéficiaire-animateurs-select" className="rounded-xl">
                    <SelectValue placeholder="Ajouter un intervenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {animateursList
                      .filter(a => !formData.animateurs_ids.includes(a.id))
                      .map((anim) => (
                        <SelectItem key={anim.id} value={anim.id}>
                          {anim.prenom} {anim.nom}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {formData.animateurs_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {formData.animateurs_ids.map((animId) => {
                      const anim = animateursList.find(a => a.id === animId);
                      return anim ? (
                        <Badge key={animId} variant="secondary" className="flex items-center gap-1 text-xs">
                          {anim.prenom} {anim.nom}
                          <X 
                            className="w-3 h-3 cursor-pointer hover:text-red-500" 
                            onClick={() => setFormData({
                              ...formData, 
                              animateurs_ids: formData.animateurs_ids.filter(id => id !== animId)
                            })}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section: Contact d'urgence */}
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <h3 className="text-sm font-bold text-red-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <span>Contact d'urgence</span>
              <span className="text-xs font-normal text-red-500">(recommandé)</span>
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="urgence_nom">Nom du contact</Label>
                <Input
                  id="urgence_nom"
                  data-testid="bénéficiaire-urgence-nom-input"
                  value={formData.contact_urgence_nom}
                  onChange={(e) => setFormData({ ...formData, contact_urgence_nom: e.target.value })}
                  className="rounded-xl"
                  placeholder="Nom et prénom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urgence_tel">Téléphone</Label>
                <Input
                  id="urgence_tel"
                  data-testid="bénéficiaire-urgence-tel-input"
                  type="tel"
                  value={formData.contact_urgence_telephone}
                  onChange={(e) => setFormData({ ...formData, contact_urgence_telephone: e.target.value })}
                  className="rounded-xl"
                  placeholder="06 XX XX XX XX"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="urgence_lien">Lien de parenté</Label>
                <Select value={formData.contact_urgence_lien} onValueChange={(val) => setFormData({ ...formData, contact_urgence_lien: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conjoint">Conjoint(e)</SelectItem>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="enfant">Enfant</SelectItem>
                    <SelectItem value="frere_soeur">Frère/Soeur</SelectItem>
                    <SelectItem value="ami">Ami(e)</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section: Situation professionnelle */}
          <div className="bg-blue-50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-blue-700 mb-3 uppercase tracking-wider">Situation professionnelle</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Situation actuelle *</Label>
                <Select value={formData.situation_actuelle} onValueChange={(val) => setFormData({ ...formData, situation_actuelle: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {SITUATIONS_ACTUELLES.map(sa => (
                      <SelectItem key={sa.value} value={sa.value}>{sa.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Niveau d'études *</Label>
                <Select value={formData.niveau_etudes} onValueChange={(val) => setFormData({ ...formData, niveau_etudes: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {NIVEAUX_ETUDES.map(ne => (
                      <SelectItem key={ne.value} value={ne.value}>{ne.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2 mt-3">
              <Label htmlFor="diplome">Diplôme obtenu</Label>
              <Input
                id="diplome"
                value={formData.diplome_obtenu}
                onChange={(e) => setFormData({ ...formData, diplome_obtenu: e.target.value })}
                className="rounded-xl"
                placeholder="Ex: CAP Cuisine, BTS Comptabilité..."
              />
            </div>

            <div className="space-y-2 mt-3">
              <Label htmlFor="objectif">Projet professionnel / Objectif *</Label>
              <Textarea
                id="objectif"
                value={formData.objectif_professionnel}
                onChange={(e) => setFormData({ ...formData, objectif_professionnel: e.target.value })}
                required
                className="rounded-xl"
                placeholder="Décrivez le projet professionnel du bénéficiaire..."
                rows={2}
              />
            </div>
          </div>

          {/* Section: Mobilité */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Mobilité</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="permis"
                  checked={formData.permis_conduire}
                  onCheckedChange={(checked) => setFormData({ ...formData, permis_conduire: !!checked })}
                />
                <label htmlFor="permis" className="text-sm cursor-pointer">Permis de conduire</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="vehicule"
                  checked={formData.vehicule}
                  onCheckedChange={(checked) => setFormData({ ...formData, vehicule: !!checked })}
                />
                <label htmlFor="vehicule" className="text-sm cursor-pointer">Véhicule personnel</label>
              </div>
              <div className="space-y-2">
                <Label>Mobilité géographique</Label>
                <Select value={formData.mobilite_geographique} onValueChange={(val) => setFormData({ ...formData, mobilite_geographique: val })}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {MOBILITES.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Bouton pour afficher plus de champs */}
          <Button
            type="button"
            variant="outline"
            className="w-full rounded-xl"
            onClick={() => setShowMoreFields(!showMoreFields)}
          >
            {showMoreFields ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                Masquer les champs supplémentaires
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                Afficher plus de champs (aides sociales, freins, secteurs...)
              </>
            )}
          </Button>

          {showMoreFields && (
            <>
              {/* Section: Aides sociales */}
              <div className="bg-amber-50 rounded-xl p-4">
                <h3 className="text-sm font-bold text-amber-700 mb-3 uppercase tracking-wider">Aides sociales perçues</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="rsa"
                      checked={formData.rsa}
                      onCheckedChange={(checked) => setFormData({ ...formData, rsa: !!checked })}
                    />
                    <label htmlFor="rsa" className="text-sm cursor-pointer">RSA</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="are"
                      checked={formData.are}
                      onCheckedChange={(checked) => setFormData({ ...formData, are: !!checked })}
                    />
                    <label htmlFor="are" className="text-sm cursor-pointer">ARE</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="ass"
                      checked={formData.ass}
                      onCheckedChange={(checked) => setFormData({ ...formData, ass: !!checked })}
                    />
                    <label htmlFor="ass" className="text-sm cursor-pointer">ASS</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="aah"
                      checked={formData.aah}
                      onCheckedChange={(checked) => setFormData({ ...formData, aah: !!checked })}
                    />
                    <label htmlFor="aah" className="text-sm cursor-pointer">AAH</label>
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  <Label htmlFor="autres_aides">Autres aides</Label>
                  <Input
                    id="autres_aides"
                    value={formData.autres_aides}
                    onChange={(e) => setFormData({ ...formData, autres_aides: e.target.value })}
                    className="rounded-xl"
                    placeholder="Précisez les autres aides..."
                  />
                </div>
              </div>

              {/* Secteurs d'activité */}
              <div className="space-y-2">
                <Label>Secteurs d'activité visés (max 3)</Label>
                <div className="grid grid-cols-3 gap-2">
                  {SECTEURS_OPTIONS.map((secteur) => (
                    <div key={secteur} className="flex items-center space-x-2">
                      <Checkbox
                        id={`secteur-${secteur}`}
                        data-testid={`secteur-${secteur}`}
                        checked={(formData.secteurs_vises || []).includes(secteur)}
                        onCheckedChange={() => toggleSecteur(secteur)}
                        disabled={!(formData.secteurs_vises || []).includes(secteur) && (formData.secteurs_vises || []).length >= 3}
                      />
                      <label htmlFor={`secteur-${secteur}`} className="text-sm cursor-pointer">
                        {secteur}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500">{(formData.secteurs_vises || []).length}/3 secteurs sélectionnés</p>

                {/* Précision si "Autre" sélectionné */}
                {(formData.secteurs_vises || []).includes('Autre') && (
                  <div className="pt-2">
                    <Label htmlFor="secteur-autre-precision" className="text-sm">
                      Précisez le secteur « Autre »
                    </Label>
                    <Input
                      id="secteur-autre-precision"
                      data-testid="secteur-autre-precision"
                      value={formData.secteur_autre_precision || ''}
                      onChange={(e) => setFormData({ ...formData, secteur_autre_precision: e.target.value })}
                      placeholder="Ex : Artisanat, restauration collective, métiers de bouche..."
                      className="mt-1"
                    />
                  </div>
                )}
              </div>

              {/* Freins */}
              <div className="space-y-2">
                <Label>Freins identifiés par le référent</Label>
                <div className="space-y-3">
                  {FREINS_OPTIONS.map((frein) => (
                    <div key={frein} className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id={`frein-${frein}`}
                          data-testid={`frein-${frein}`}
                          checked={formData.freins[frein] !== undefined}
                          onCheckedChange={() => toggleFrein(frein)}
                        />
                        <label htmlFor={`frein-${frein}`} className="text-sm font-medium cursor-pointer">
                          {frein}
                        </label>
                      </div>
                      {formData.freins[frein] !== undefined && (
                        <Input
                          placeholder={`Précisions sur ${frein}...`}
                          value={formData.freins[frein]}
                          onChange={(e) => updateFreinDetail(frein, e.target.value)}
                          className="rounded-xl ml-6"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Commentaires */}
              <div className="space-y-2">
                <Label htmlFor="commentaires">Commentaires / Notes</Label>
                <Textarea
                  id="commentaires"
                  value={formData.commentaires}
                  onChange={(e) => setFormData({ ...formData, commentaires: e.target.value })}
                  className="rounded-xl"
                  placeholder="Informations complémentaires..."
                  rows={3}
                />
              </div>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button type="submit" data-testid="bénéficiaire-submit-button" disabled={loading} className="rounded-xl bg-blue-600 hover:bg-blue-700">
              {loading ? (editMode ? 'Modification...' : 'Création...') : (editMode ? 'Enregistrer les modifications' : 'Créer le bénéficiaire')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddApprenantDialog;